// ========================
// Waypoint & Town Portal System
// ========================

import { ZoneType, ZONES } from './Zone';

export interface WaypointState {
  activatedWaypoints: Set<ZoneType>;
  lastTown: ZoneType | null;
  portalActive: boolean;
  portalDestination: ZoneType | null;
  // Return portal - where to go back after using town portal
  returnPortalActive: boolean;
  returnPortalDestination: ZoneType | null;
}

// Extend zone info with waypoint data
export interface WaypointZoneInfo {
  zoneId: ZoneType;
  name: string;
  hasWaypoint: boolean;
  isTown: boolean;
  levelRange: { min: number; max: number };
  activated: boolean;
}

// Define which zones have waypoints and which are towns
export const WAYPOINT_ZONES: Partial<Record<ZoneType, { hasWaypoint: boolean; isTown: boolean }>> = {
  // Act 1
  'otag_koyu': { hasWaypoint: true, isTown: true },
  'karanlik_orman': { hasWaypoint: true, isTown: false },
  'ruh_batakligi': { hasWaypoint: true, isTown: false },
  'erlik_tapinagi': { hasWaypoint: true, isTown: false },

  // Act 2
  'ergenekon_girisi': { hasWaypoint: true, isTown: false },
  'firtina_ovasi': { hasWaypoint: true, isTown: false },
  'ates_magarasi': { hasWaypoint: true, isTown: false },
  'yersub_tahti': { hasWaypoint: true, isTown: false },

  // Act 3
  'karanlik_tunel': { hasWaypoint: true, isTown: false },
  'olum_nehri': { hasWaypoint: false, isTown: false },
  'kemik_ormani': { hasWaypoint: true, isTown: false },
  'lanetli_saray': { hasWaypoint: true, isTown: false },

  // Act 4
  'erlik_kapisi': { hasWaypoint: true, isTown: false },
  'cehennem_bahcesi': { hasWaypoint: false, isTown: false },
  'taht_odasi': { hasWaypoint: true, isTown: false },
};

export class WaypointManager {
  private state: WaypointState;
  private onTeleport?: (destination: ZoneType) => void;

  constructor() {
    this.state = {
      activatedWaypoints: new Set(['otag_koyu']), // Start with town waypoint active
      lastTown: 'otag_koyu',
      portalActive: false,
      portalDestination: null,
      returnPortalActive: false,
      returnPortalDestination: null,
    };
  }

  // Set teleport callback
  setTeleportCallback(callback: (destination: ZoneType) => void): void {
    this.onTeleport = callback;
  }

  // Check if zone has waypoint
  hasWaypoint(zoneId: ZoneType): boolean {
    return WAYPOINT_ZONES[zoneId]?.hasWaypoint ?? false;
  }

  // Check if zone is a town
  isTown(zoneId: ZoneType): boolean {
    return WAYPOINT_ZONES[zoneId]?.isTown ?? false;
  }

  // Activate waypoint in current zone
  activateWaypoint(zoneId: ZoneType): boolean {
    if (!this.hasWaypoint(zoneId)) {
      return false;
    }

    if (this.state.activatedWaypoints.has(zoneId)) {
      return false; // Already activated
    }

    this.state.activatedWaypoints.add(zoneId);

    // If this is a town, update last town
    if (this.isTown(zoneId)) {
      this.state.lastTown = zoneId;
    }

    return true;
  }

  // Check if waypoint is activated
  isWaypointActivated(zoneId: ZoneType): boolean {
    return this.state.activatedWaypoints.has(zoneId);
  }

  // Get all activated waypoints
  getActivatedWaypoints(): WaypointZoneInfo[] {
    const waypoints: WaypointZoneInfo[] = [];

    for (const [zoneId, info] of Object.entries(WAYPOINT_ZONES)) {
      if (info.hasWaypoint) {
        const zone = ZONES[zoneId as ZoneType];
        if (zone) {
          waypoints.push({
            zoneId: zoneId as ZoneType,
            name: zone.name,
            hasWaypoint: true,
            isTown: info.isTown,
            levelRange: zone.levelRange,
            activated: this.state.activatedWaypoints.has(zoneId as ZoneType),
          });
        }
      }
    }

    return waypoints;
  }

  // Get only activated waypoints for teleport menu
  getAvailableDestinations(): WaypointZoneInfo[] {
    return this.getActivatedWaypoints().filter(w => w.activated);
  }

  // Teleport to waypoint
  teleportToWaypoint(zoneId: ZoneType): boolean {
    if (!this.state.activatedWaypoints.has(zoneId)) {
      return false;
    }

    if (this.onTeleport) {
      this.onTeleport(zoneId);
    }

    // Update last town if teleporting to a town
    if (this.isTown(zoneId)) {
      this.state.lastTown = zoneId;
    }

    return true;
  }

  // Open town portal (T key) - fromZone is where the player currently is
  openTownPortal(fromZone: ZoneType): boolean {
    if (!this.state.lastTown) {
      return false;
    }

    // Don't open portal if already in town
    if (this.isTown(fromZone)) {
      return false;
    }

    this.state.portalActive = true;
    this.state.portalDestination = this.state.lastTown;
    // Remember where we came from for return portal
    this.state.returnPortalDestination = fromZone;
    return true;
  }

  // Use the town portal - teleport to town and activate return portal
  useTownPortal(): ZoneType | null {
    if (!this.state.portalActive || !this.state.portalDestination) {
      return null;
    }

    const destination = this.state.portalDestination;
    this.state.portalActive = false;
    this.state.portalDestination = null;

    // Activate return portal in town
    this.state.returnPortalActive = true;

    return destination;
  }

  // Use return portal (in town) - go back to where you opened the portal
  useReturnPortal(): ZoneType | null {
    if (!this.state.returnPortalActive || !this.state.returnPortalDestination) {
      return null;
    }

    const destination = this.state.returnPortalDestination;
    this.state.returnPortalActive = false;
    this.state.returnPortalDestination = null;

    return destination;
  }

  // Close town portal (without using it)
  closePortal(): void {
    this.state.portalActive = false;
    this.state.portalDestination = null;
  }

  // Close return portal
  closeReturnPortal(): void {
    this.state.returnPortalActive = false;
    this.state.returnPortalDestination = null;
  }

  // Check if town portal is active
  isPortalActive(): boolean {
    return this.state.portalActive;
  }

  // Check if return portal is active (in town)
  isReturnPortalActive(): boolean {
    return this.state.returnPortalActive;
  }

  // Get town portal destination
  getPortalDestination(): ZoneType | null {
    return this.state.portalDestination;
  }

  // Get return portal destination
  getReturnPortalDestination(): ZoneType | null {
    return this.state.returnPortalDestination;
  }

  // Get last town
  getLastTown(): ZoneType | null {
    return this.state.lastTown;
  }

  // Update last town when entering a town
  enterZone(zoneId: ZoneType): void {
    if (this.isTown(zoneId)) {
      this.state.lastTown = zoneId;
    }
  }

  // Serialize state for saving
  serialize(): object {
    return {
      activatedWaypoints: Array.from(this.state.activatedWaypoints),
      lastTown: this.state.lastTown,
    };
  }

  // Deserialize state from save
  deserialize(data: { activatedWaypoints?: string[]; lastTown?: string }): void {
    if (data.activatedWaypoints) {
      this.state.activatedWaypoints = new Set(data.activatedWaypoints as ZoneType[]);
    }
    if (data.lastTown) {
      this.state.lastTown = data.lastTown as ZoneType;
    }
  }
}

// Singleton instance
let waypointManagerInstance: WaypointManager | null = null;

export function getWaypointManager(): WaypointManager {
  if (!waypointManagerInstance) {
    waypointManagerInstance = new WaypointManager();
  }
  return waypointManagerInstance;
}

export function resetWaypointManager(): void {
  waypointManagerInstance = new WaypointManager();
}
